import React from "react";
import { Styled } from "../../styles";
import { Button, Grid, Typography, MenuItem, FormControl, InputLabel } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { Select, Input } from "@dippi/ui";
import { Forms } from "@/types/form";
import { yupResolver } from "@hookform/resolvers/yup";
import schemas from "@/utils/schemas";
import constant from "@/utils/constant";
import { SELECT_ITEMS } from "@/utils/helpers";

interface Props {
    setStep: React.Dispatch<React.SetStateAction<number>>;
    setState: React.Dispatch<
        React.SetStateAction<{
            step1: Forms["OrganizationStep1"] | null;
            step2: Forms["OrganizationStep2"] | null;
        }>
    >;
    state: { step1: Forms["OrganizationStep1"] | null; step2: Forms["OrganizationStep2"] | null };
}

const Form: React.FC<Props> = ({ setStep, setState, state }) => {
    const { control, handleSubmit } = useForm<Forms["OrganizationStep1"]>({
        resolver: yupResolver(schemas.organization.step1),
        defaultValues: state.step1 ?? {
            industry: "AB",
            country: "GE",
            title: "",
            companyId: "",
            business: "producer",
        },
    });

    const onContinue = (data: Forms["OrganizationStep1"]) => {
        setState(state => ({ ...state, step1: data }));
        setStep(2);
    };

    return (
        <Styled.Form onSubmit={handleSubmit(onContinue)}>
            <Grid container rowGap={constant.GRID_SPACING}>
                <Grid item xs={12}>
                    <Typography variant="h3" textAlign="center">
                        Create your organization
                    </Typography>
                </Grid>
                <Grid item xs={12}>
                    <Select
                        name="industry"
                        control={control}
                        tabIndex={1}
                        items={SELECT_ITEMS.industries}
                        label="Industry"
                        data-testid="organization-industry-select"
                    />
                </Grid>
                <Grid item xs={12}>
                    <FormControl fullWidth={true}>
                        <InputLabel>Country</InputLabel>
                        <Controller
                            name="country"
                            control={control}
                            render={({ field: { value, onChange } }) => {
                                return (
                                    <Styled.CountrySelector
                                        value={value}
                                        onChange={onChange}
                                        label="Country"
                                        tabIndex={2}
                                        data-testid="organization-country-select"
                                    >
                                        {SELECT_ITEMS.countries.map(i => {
                                            const isoCode = i.value;
                                            const title = i.label;
                                            return (
                                                <MenuItem key={isoCode} value={isoCode}>
                                                    <img
                                                        src={`${constant.VITE_CDN_BUCKET_PATH}/static/flags/${isoCode}.png`}
                                                        alt={`${title} flag`}
                                                        height={25}
                                                        width={25}
                                                    />
                                                    <Typography
                                                        fontFamily="Figtree"
                                                        marginLeft="10px"
                                                    >
                                                        {title}
                                                    </Typography>
                                                </MenuItem>
                                            );
                                        })}
                                    </Styled.CountrySelector>
                                );
                            }}
                        />
                    </FormControl>
                </Grid>
                <Grid item xs={12}>
                    <Select
                        name="business"
                        control={control}
                        tabIndex={3}
                        items={SELECT_ITEMS.business}
                        label="Line of business"
                        data-testid="organization-business-select"
                    />
                </Grid>
                <Grid item xs={12}>
                    <Input
                        name="companyId"
                        control={control}
                        label="Organization ID"
                        tabIndex={4}
                        testId="organization-company-id-input"
                    />
                </Grid>
                <Grid item xs={12}>
                    <Input
                        name="title"
                        control={control}
                        label="Organization name"
                        tabIndex={5}
                        testId="organization-company-name-input"
                    />
                </Grid>
                <Grid item xs={12}>
                    <Button
                        fullWidth={true}
                        type="submit"
                        tabIndex={5}
                        data-testid="organization-continue-button"
                    >
                        Continue
                    </Button>
                </Grid>
            </Grid>
        </Styled.Form>
    );
};

export default Form;
